import React, { useMemo, useState, useEffect } from "react";
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import api from "../../../../middleware/api";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../Common/Loading/LoadingSpinner";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { formatDateWithTimezone } from "../../../../utils/dateFormatter";
const TransactionBadge = ({ type }) => {
    return (
        <span
            className={`badge ${type === "spend"
                ? "badge-warning badge-lg"
                : "badge-primary badge-lg"
                } text-xs`}
        >
            {type === "spend" ? "Dépense" : "Recharge"}
        </span>
    )
}

const TransactionsTable = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sorting, setSorting] = useState([]);

    // Fetch transactions au montage du composant
    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                const response = await api.get("/transactions");
                if (!response.data.success) {
                    toast.error(response.data.message);
                } else {
                    setTransactions(response.data.transactions); // Supposons que les données sont dans `data.data`
                }
            } catch (error) {
                console.error("Erreur:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, []);

    // Définition des colonnes
    const columns = useMemo(
        () => [
            {
                accessorKey: "type",
                header: "Type",
                cell: (info) => (
                    <TransactionBadge type={info.getValue()} />
                ),
            },
            {
                accessorKey: "amount",
                header: "Montant",
                cell: (info) => `${info.getValue()} credits`,
            },
            {
                accessorKey: "balanceAfter",
                header: "Solde après",
                cell: (info) => `${info.getValue()} credits`,
            },
            {
                accessorKey: "initiator",
                header: "Initiateur",
                cell: (info) =>
                    info.getValue() === "admin" ? "Administrateur" : "Vous",
            },
            {
                accessorKey: "description",
                header: "Motif",
                cell: (info) => info.getValue() || "N/A",
            },
            {
                accessorKey: "createdAt",
                header: "Date de la transaction",
                cell: (info) => formatDateWithTimezone(info.getValue()),
            },
        ],
        []
    );

    const table = useReactTable({
        data: transactions,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (loading) {
        return <LoadingSpinner />
    }
    if (!loading && transactions.length === 0) {
        return (
            <div className="flex items-center justify-center">
                <p>Aucune transaction trouvée</p>
            </div>
        )
    }

    return (
        <div className="overflow-hidden rounded-lg shadow-lg">
            <table className="table table-zebra w-full border-collapse">
                <thead className="bg-primary text-white">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className="py-3 px-4 text-center cursor-pointer"
                                    onClick={
                                        header.column.getCanSort()
                                            ? header.column.getToggleSortingHandler()
                                            : undefined
                                    }
                                >
                                    {flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                                    {header.column.getIsSorted() ? (
                                        header.column.getIsSorted() === "asc" ? (
                                            <ChevronUpIcon className="w-4 h-4" />
                                        ) : (
                                            <ChevronDownIcon className="w-4 h-4" />
                                        )
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="hover:bg-base-200">
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    key={cell.id}
                                    className="py-3 px-4 text-center"
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TransactionsTable;
